import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useConfetti } from '../hooks/useConfetti';
import { FiRefreshCw, FiUser, FiX, FiArrowUp, FiArrowDown } from 'react-icons/fi';
import { IconType } from 'react-icons';
import { dashboardService } from '../api/services/dashboardService';
import { UserDetail, PaginatedResponse, TableCountsResponse, ProfileTreeResponse } from '../api/types';
import { getUserFriendlyErrorMessage } from '../api/utils/errorHandler';

interface User {
  id: number;
  playerId: string;
  email: string;
  userType: string;
  countryCode: string;
  testSessionCount: number;
  gameSessionCount: number;
  chatSessionCount: number;
}

const Users: React.FC = () => {
  const { triggerConfetti } = useConfetti();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const itemsPerPage = 25;
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 0,
  });
  const [tableCounts, setTableCounts] = useState<TableCountsResponse | null>(null);
  const previousTotalRef = useRef<number>(0);
  const [profileTreeModal, setProfileTreeModal] = useState<{
    isOpen: boolean;
    playerId: string;
    testData: ProfileTreeResponse | null;
    profileData: ProfileTreeResponse | null;
    loading: boolean;
  }>({
    isOpen: false,
    playerId: '',
    testData: null,
    profileData: null,
    loading: false,
  });
  const [showCounter, setShowCounter] = useState<boolean>(false);
  const [counterValue, setCounterValue] = useState<number>(0);
  const [previousCounterValue, setPreviousCounterValue] = useState<number>(0);
  const [totalUsers, setTotalUsers] = useState<number>(0);
  const counterIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const renderIcon = (Icon: IconType, className: string) => {
    const IconComponent = Icon as React.ComponentType<{ className?: string }>;
    return <IconComponent className={className} />;
  };

  // Handle sort
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  // Sort users
  const sortedUsers = useMemo(() => {
    if (!sortColumn) return users;

    return [...users].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortColumn) {
        case 'id':
          aValue = a.id;
          bValue = b.id;
          break;
        case 'playerId':
          aValue = a.playerId.toLowerCase();
          bValue = b.playerId.toLowerCase();
          break;
        case 'userType':
          aValue = a.userType.toLowerCase();
          bValue = b.userType.toLowerCase();
          break;
        case 'countryCode':
          aValue = a.countryCode.toLowerCase();
          bValue = b.countryCode.toLowerCase();
          break;
        case 'testSessionCount':
          aValue = a.testSessionCount;
          bValue = b.testSessionCount;
          break;
        case 'gameSessionCount':
          aValue = a.gameSessionCount;
          bValue = b.gameSessionCount;
          break;
        case 'chatSessionCount':
          aValue = a.chatSessionCount;
          bValue = b.chatSessionCount;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [users, sortColumn, sortDirection]);

  // Fetch users from API
  const fetchUsers = async (page: number = 1, showLoading: boolean = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      setError(null);

      const response: PaginatedResponse<UserDetail> = await dashboardService.getUserDetails({
        page,
      });

      // Transform API users to local User format
      const transformedUsers: User[] = response.data.map((userDetail) => ({
        id: userDetail.numeric_id,
        playerId: userDetail.player_id,
        email: userDetail.email || '-',
        userType: userDetail.user_type,
        countryCode: userDetail.country_code || '-',
        testSessionCount: userDetail.test_session_count || 0,
        gameSessionCount: userDetail.game_session_count || 0,
        chatSessionCount: userDetail.chat_session_count || 0,
      }));

      const newTotal = response.pagination.total;
      const oldTotal = previousTotalRef.current;
      
      console.log(`[Users] Previous total: ${oldTotal}, New total: ${newTotal}`);
      
      // Check if user count increased (only trigger if we had a previous value and it increased)
      if (oldTotal > 0 && newTotal > oldTotal) {
        console.log(`🎉 User count increased from ${oldTotal} to ${newTotal}! Triggering confetti...`);
        try {
    triggerConfetti();
          console.log('✅ Confetti triggered successfully');
        } catch (error) {
          console.error('❌ Error triggering confetti:', error);
        }
      } else if (oldTotal === 0 && newTotal > 0) {
        console.log(`[Users] Initial load: ${newTotal} users`);
      } else if (newTotal === oldTotal) {
        console.log(`[Users] User count unchanged: ${newTotal}`);
      } else if (newTotal < oldTotal) {
        console.log(`[Users] User count decreased from ${oldTotal} to ${newTotal}`);
      }
      
      // Update the ref for next comparison
      previousTotalRef.current = newTotal;
      setUsers(transformedUsers);
      setPagination({
        total: newTotal,
        totalPages: response.pagination.totalPages,
      });
    } catch (err: any) {
      const errorMessage = getUserFriendlyErrorMessage(err);
      setError(errorMessage);
      console.error('Error fetching users:', err);
    } finally {
      if (showLoading) {
        setLoading(false);
      }
      setIsRefreshing(false);
    }
  };

  // Fetch table counts for stats
  const fetchTableCounts = async () => {
    try {
      const response = await dashboardService.getTableCounts();
      setTableCounts(response);
    } catch (err: any) {
      console.error('Error fetching table counts:', err);
    }
  };

  useEffect(() => {
    fetchUsers(currentPage);
    fetchTableCounts();
  }, [currentPage]);

  // Poll API every 30 seconds to check for new users (without showing loading)
  useEffect(() => {
    const interval = setInterval(() => {
      fetchUsers(currentPage, false); // Don't show loading spinner on polling
      fetchTableCounts(); // Also update table counts to refresh daily user count
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const handleRefresh = () => {
    setIsRefreshing(true);
    fetchUsers(currentPage);
    fetchTableCounts();
  };

  // Calculate percentage change
  const calculateChange = (current: number, previous: number): string => {
    if (previous === 0) return '+0%';
    const change = ((current - previous) / previous) * 100;
    return `${change >= 0 ? '+' : ''}${change.toFixed(1)}%`;
  };

  // Get daily change from metrics (daily growth percentage)
  const getDailyChange = (metricType: string): string => {
    if (!tableCounts || !tableCounts.metrics) return '+0%';
    const key = `${metricType}_daily`;
    const metric = tableCounts.metrics[key];
    if (!metric) return '+0%';
    const change = metric.growth_percentage;
    return `${change >= 0 ? '+' : ''}${change.toFixed(1)}%`;
  };

  // Get weekly change from metrics (weekly growth percentage)
  const getWeeklyChange = (metricType: string): string => {
    if (!tableCounts || !tableCounts.metrics) return '+0%';
    const key = `${metricType}_weekly`;
    const metric = tableCounts.metrics[key];
    if (!metric) return '+0%';
    const change = metric.growth_percentage;
    return `${change >= 0 ? '+' : ''}${change.toFixed(1)}%`;
  };

  // Get monthly change from metrics (monthly growth percentage)
  const getMonthlyChange = (metricType: string): string => {
    if (!tableCounts || !tableCounts.metrics) return '+0%';
    const key = `${metricType}_monthly`;
    const metric = tableCounts.metrics[key];
    if (!metric) return '+0%';
    const change = metric.growth_percentage;
    return `${change >= 0 ? '+' : ''}${change.toFixed(1)}%`;
  };

  // Get daily change from Daily metrics (daily growth percentage)
  const getDailyValue = (metricType: string): string => {
    if (!tableCounts || !tableCounts.metrics) return '+0%';
    const key = `${metricType}_daily`;
    const metric = tableCounts.metrics[key];
    if (!metric) return '+0%';
    const change = metric.growth_percentage;
    return `${change >= 0 ? '+' : ''}${change.toFixed(1)}%`;
  };

  // Get today's count by metric type
  const getTodayCount = (metricType: string): number => {
    if (!tableCounts) return 0;
    const count = tableCounts.total_counts.find(tc => tc.metric_type === metricType);
    return count?.total_today || 0;
  };

  const stats = [
    { 
      label: 'Toplam Kullanıcı', 
      value: pagination.total.toLocaleString(), 
      change: getDailyChange('users'), 
      weeklyChange: getWeeklyChange('users'),
      monthlyChange: getMonthlyChange('users'),
      color: 'blue' 
    },
    { 
      label: 'Kullanıcı', 
      value: getTodayCount('users').toLocaleString('tr-TR'), 
      color: 'green' 
    },
  ];

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Fetch profile tree data (both test and profile)
  const fetchProfileTree = async (playerId: string) => {
    setProfileTreeModal({
      isOpen: true,
      playerId,
      testData: null,
      profileData: null,
      loading: true,
    });

    try {
      // Fetch both test and profile data in parallel
      const [testResponse, profileResponse] = await Promise.all([
        dashboardService.getProfileTree({
          player_id: playerId,
          type: 'tests',
        }),
        dashboardService.getProfileTree({
          player_id: playerId,
          type: 'profile',
        }),
      ]);

      // Process test data
      const testData = Array.isArray(testResponse) ? (testResponse[0] || {}) : testResponse;
      
      // Process profile data
      const profileData = Array.isArray(profileResponse) ? (profileResponse[0] || {}) : profileResponse;

      setProfileTreeModal(prev => ({
        ...prev,
        testData: testData as ProfileTreeResponse,
        profileData: profileData as ProfileTreeResponse,
        loading: false,
      }));
    } catch (err: any) {
      console.error('Error fetching profile tree:', err);
      setProfileTreeModal(prev => ({
        ...prev,
        testData: {},
        profileData: {},
        loading: false,
      }));
    }
  };

  const closeProfileTreeModal = () => {
    setProfileTreeModal({
      isOpen: false,
      playerId: '',
      testData: null,
      profileData: null,
      loading: false,
    });
  };

  // Fetch table counts for counter
  const fetchCounterData = async () => {
    try {
      const response = await dashboardService.getTableCounts();
      const userCount = response.total_counts.find(tc => tc.metric_type === 'users');
      const todayCount = userCount?.total_today || 0;
      const total = userCount?.total || 0;
      
      setTotalUsers(total);
      
      if (showCounter) {
        // Animate counter if value increased
        if (todayCount > counterValue) {
          setPreviousCounterValue(counterValue);
          setCounterValue(todayCount);
        } else if (todayCount !== counterValue) {
          setCounterValue(todayCount);
        }
      } else {
        setCounterValue(todayCount);
      }
    } catch (err: any) {
      console.error('Error fetching counter data:', err);
    }
  };

  // Poll for new users when counter is open
  useEffect(() => {
    if (showCounter) {
      // Fetch immediately
      fetchCounterData();
      
      // Then poll every 5 seconds
      const interval = setInterval(() => {
        fetchCounterData();
      }, 5000);
      
      counterIntervalRef.current = interval;
      
      return () => {
        if (counterIntervalRef.current) {
          clearInterval(counterIntervalRef.current);
          counterIntervalRef.current = null;
        }
      };
    } else {
      if (counterIntervalRef.current) {
        clearInterval(counterIntervalRef.current);
        counterIntervalRef.current = null;
      }
    }
  }, [showCounter]);

  // Initialize counter value on mount
  useEffect(() => {
    if (tableCounts) {
      const userCount = tableCounts.total_counts.find(tc => tc.metric_type === 'users');
      const todayCount = userCount?.total_today || 0;
      const total = userCount?.total || 0;
      setCounterValue(todayCount);
      setTotalUsers(total);
    }
  }, [tableCounts]);

  return (
    <div className="w-full max-w-7xl mx-auto space-y-6">
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 dark:from-slate-200 dark:to-slate-400 bg-clip-text text-transparent mb-2">
            Kullanıcılar
          </h1>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowCounter(true)}
              className="px-6 py-3 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded-xl hover:bg-gray-300 dark:hover:bg-gray-600 transition-all duration-200 font-semibold shadow-lg hover:shadow-xl hover:scale-105 transform"
            >
              Sayaç
            </button>
            <button
              onClick={handleRefresh}
              disabled={isRefreshing || loading}
              className="flex items-center gap-2 px-6 py-3 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded-xl hover:bg-gray-300 dark:hover:bg-gray-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-semibold shadow-lg hover:shadow-xl hover:scale-105 transform disabled:hover:scale-100"
            >
              {renderIcon(FiRefreshCw, `w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`)}
              <span>Yenile</span>
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="glass-strong bg-gradient-to-r from-red-50/80 to-rose-50/80 dark:from-red-900/30 dark:to-rose-900/30 border border-red-200/50 dark:border-red-800/50 rounded-2xl p-4 mb-6 shadow-lg animate-slide-up">
          <div className="flex items-center gap-3 text-red-700 dark:text-red-300">
            <span className="text-2xl">⚠️</span>
            <p className="font-semibold">{error}</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        {stats.map((stat, index) => (
          <div key={index} className={`relative glass-strong rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 p-6 border overflow-hidden group transform hover:-translate-y-1 animate-fade-in ${
            stat.color === 'blue' ? 'hover:border-blue-300 dark:hover:border-blue-600' :
            stat.color === 'green' ? 'hover:border-green-300 dark:hover:border-green-600' :
            'hover:border-purple-300 dark:hover:border-purple-600'
          }`} style={{ animationDelay: `${index * 100}ms` }}>
            {/* Animated gradient background */}
            <div className={`absolute -top-20 -right-20 w-40 h-40 rounded-full blur-3xl opacity-30 group-hover:opacity-50 group-hover:scale-150 transition-all duration-700 ${
              stat.color === 'blue' ? 'bg-blue-100 dark:bg-blue-900/30' :
              stat.color === 'green' ? 'bg-green-100 dark:bg-green-900/30' :
              'bg-purple-100 dark:bg-purple-900/30'
            }`}></div>
            <div className={`absolute -bottom-10 -left-10 w-32 h-32 rounded-full blur-2xl opacity-20 group-hover:opacity-40 transition-all duration-700 ${
              stat.color === 'blue' ? 'bg-blue-100 dark:bg-blue-900/30' :
              stat.color === 'green' ? 'bg-green-100 dark:bg-green-900/30' :
              'bg-purple-100 dark:bg-purple-900/30'
            }`}></div>
            
            {/* Shimmer effect */}
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
              <div className="absolute inset-0 shimmer"></div>
            </div>
            
            <div className="relative z-10">
              <p className="text-sm font-bold text-slate-600 dark:text-slate-300 mb-3 uppercase tracking-wider">{stat.label}</p>
              <p className={`text-5xl font-extrabold mb-4 bg-gradient-to-r ${
                stat.color === 'blue' ? 'from-blue-600 via-blue-500 to-blue-400' :
                stat.color === 'green' ? 'from-green-600 via-green-500 to-green-400' :
                'from-purple-600 via-purple-500 to-purple-400'
              } bg-clip-text text-transparent group-hover:scale-105 transition-transform duration-300`}>
                {stat.value}
              </p>
              {stat.label === 'Toplam Kullanıcı' && (
                <div className="flex flex-col gap-3 pt-4 border-t border-gray-200/50 dark:border-gray-700/50">
                  {stat.change && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Günlük</span>
                      <span className={`text-sm font-bold px-3 py-1.5 rounded-lg shadow-sm transition-all duration-300 ${
                        stat.change.startsWith('+') || stat.change.startsWith('0')
                          ? 'bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800'
                          : 'bg-gradient-to-r from-red-50 to-rose-50 dark:from-red-900/30 dark:to-rose-900/30 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800'
                      }`}>
                        {stat.change}
                      </span>
                    </div>
                  )}
                  {stat.weeklyChange && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Haftalık</span>
                      <span className={`text-sm font-bold px-3 py-1.5 rounded-lg shadow-sm transition-all duration-300 ${
                        stat.weeklyChange.startsWith('+') || stat.weeklyChange.startsWith('0')
                          ? 'bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800'
                          : 'bg-gradient-to-r from-red-50 to-rose-50 dark:from-red-900/30 dark:to-rose-900/30 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800'
                      }`}>
                        {stat.weeklyChange}
                      </span>
                    </div>
                  )}
                  {stat.monthlyChange && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Aylık</span>
                      <span className={`text-sm font-bold px-3 py-1.5 rounded-lg shadow-sm transition-all duration-300 ${
                        stat.monthlyChange.startsWith('+') || stat.monthlyChange.startsWith('0')
                          ? 'bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800'
                          : 'bg-gradient-to-r from-red-50 to-rose-50 dark:from-red-900/30 dark:to-rose-900/30 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800'
                      }`}>
                        {stat.monthlyChange}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="glass-strong rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 overflow-hidden border transform hover:scale-[1.01] animate-fade-in">
        <div className="px-6 py-5 border-b border-gray-200/50 dark:border-gray-700/50 bg-gradient-to-r from-indigo-50 via-purple-50 to-pink-50 dark:from-indigo-900/20 dark:via-purple-900/20 dark:to-pink-900/20 backdrop-blur-sm">
          <h2 className="text-xl font-bold gradient-text">Users</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gradient-to-r from-slate-50 to-gray-50 dark:from-gray-700 dark:to-gray-800">
              <tr>
                <th 
                  className="px-6 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors select-none"
                  onClick={() => handleSort('id')}
                >
                  <div className="flex items-center gap-2">
                    <span>ID</span>
                    {sortColumn === 'id' && renderIcon(sortDirection === 'asc' ? FiArrowUp : FiArrowDown, "w-4 h-4 text-indigo-600 dark:text-indigo-400")}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors select-none"
                  onClick={() => handleSort('playerId')}
                >
                  <div className="flex items-center gap-2">
                    <span>Player ID</span>
                    {sortColumn === 'playerId' && renderIcon(sortDirection === 'asc' ? FiArrowUp : FiArrowDown, "w-4 h-4 text-indigo-600 dark:text-indigo-400")}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors select-none"
                  onClick={() => handleSort('userType')}
                >
                  <div className="flex items-center gap-2">
                    <span>Kullanıcı Tipi</span>
                    {sortColumn === 'userType' && renderIcon(sortDirection === 'asc' ? FiArrowUp : FiArrowDown, "w-4 h-4 text-indigo-600 dark:text-indigo-400")}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors select-none"
                  onClick={() => handleSort('countryCode')}
                >
                  <div className="flex items-center gap-2">
                    <span>Ülke</span>
                    {sortColumn === 'countryCode' && renderIcon(sortDirection === 'asc' ? FiArrowUp : FiArrowDown, "w-4 h-4 text-indigo-600 dark:text-indigo-400")}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors select-none"
                  onClick={() => handleSort('testSessionCount')}
                >
                  <div className="flex items-center gap-2">
                    <span>Test</span>
                    {sortColumn === 'testSessionCount' && renderIcon(sortDirection === 'asc' ? FiArrowUp : FiArrowDown, "w-4 h-4 text-indigo-600 dark:text-indigo-400")}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors select-none"
                  onClick={() => handleSort('gameSessionCount')}
                >
                  <div className="flex items-center gap-2">
                    <span>Oyun</span>
                    {sortColumn === 'gameSessionCount' && renderIcon(sortDirection === 'asc' ? FiArrowUp : FiArrowDown, "w-4 h-4 text-indigo-600 dark:text-indigo-400")}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors select-none"
                  onClick={() => handleSort('chatSessionCount')}
                >
                  <div className="flex items-center gap-2">
                    <span>Chat</span>
                    {sortColumn === 'chatSessionCount' && renderIcon(sortDirection === 'asc' ? FiArrowUp : FiArrowDown, "w-4 h-4 text-indigo-600 dark:text-indigo-400")}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">İşlemler</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
              {loading && users.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center">
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                    </div>
                  </td>
                </tr>
              ) : sortedUsers.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center text-slate-500 dark:text-slate-400">
                    Kullanıcı bulunamadı
                  </td>
                </tr>
              ) : (
                sortedUsers.map((user) => (
                <tr key={user.id} className="hover:bg-gradient-to-r hover:from-indigo-50/50 hover:to-purple-50/50 dark:hover:from-indigo-900/20 dark:hover:to-purple-900/20 transition-all duration-300 border-b border-gray-100/50 dark:border-gray-700/50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900 dark:text-white">{user.id}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-slate-600 dark:text-slate-300">{user.playerId}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                      user.userType === 'PREMIUM' 
                        ? 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200' 
                        : 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200'
                    }`}>
                      {user.userType}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600 dark:text-slate-300">
                    {user.countryCode !== '-' ? (
                      <span className="px-2 py-1 text-xs font-semibold rounded bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-200">
                        {user.countryCode}
                      </span>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600 dark:text-slate-300 text-center">
                    {user.testSessionCount > 0 ? (
                      <span className="px-2 py-1 text-xs font-semibold rounded bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-200">
                        {user.testSessionCount}
                      </span>
                    ) : (
                      '0'
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600 dark:text-slate-300 text-center">
                    {user.gameSessionCount > 0 ? (
                      <span className="px-2 py-1 text-xs font-semibold rounded bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-200">
                        {user.gameSessionCount}
                      </span>
                    ) : (
                      '0'
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600 dark:text-slate-300 text-center">
                    {user.chatSessionCount > 0 ? (
                      <span className="px-2 py-1 text-xs font-semibold rounded bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-200">
                        {user.chatSessionCount}
                      </span>
                    ) : (
                      '0'
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600 dark:text-slate-300">
                    <button
                      onClick={() => fetchProfileTree(user.playerId)}
                      className="px-4 py-2 text-xs font-semibold text-white bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 dark:from-indigo-500 dark:via-purple-500 dark:to-pink-500 rounded-xl hover:from-indigo-700 hover:via-purple-700 hover:to-pink-700 dark:hover:from-indigo-600 dark:hover:via-purple-600 dark:hover:to-pink-600 transition-all duration-300 flex items-center gap-2 shadow-lg hover:shadow-xl hover:scale-105 transform"
                      title="Profile Tree - Test ve Profile Verileri"
                    >
                      {renderIcon(FiUser, 'w-4 h-4')}
                      <span>Detay</span>
                    </button>
                  </td>
                </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <div className="text-sm text-slate-600 dark:text-slate-400">
              Toplam {pagination.total} kayıttan sayfa {currentPage} / {pagination.totalPages}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1 || loading}
                className="px-4 py-2 text-sm font-semibold text-slate-700 dark:text-slate-300 glass rounded-xl hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 dark:hover:from-indigo-900/30 dark:hover:to-purple-900/30 border border-gray-300/50 dark:border-gray-600/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 hover:scale-105 transform disabled:hover:scale-100 shadow-sm hover:shadow-md"
              >
                Önceki
              </button>
              <div className="flex items-center gap-2">
                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                  let pageNum: number;
                  if (pagination.totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= pagination.totalPages - 2) {
                    pageNum = pagination.totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                  <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      disabled={loading}
                    className={`px-4 py-2 text-sm font-semibold rounded-xl transition-all duration-300 ${
                        currentPage === pageNum
                        ? 'bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 dark:from-indigo-500 dark:via-purple-500 dark:to-pink-500 text-white shadow-lg scale-110'
                        : 'text-slate-700 dark:text-slate-300 glass border border-gray-300/50 dark:border-gray-600/50 hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 dark:hover:from-indigo-900/30 dark:hover:to-purple-900/30 hover:scale-105 transform shadow-sm hover:shadow-md'
                    }`}
                  >
                      {pageNum}
                  </button>
                  );
                })}
              </div>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === pagination.totalPages || loading}
                className="px-4 py-2 text-sm font-semibold text-slate-700 dark:text-slate-300 glass rounded-xl hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 dark:hover:from-indigo-900/30 dark:hover:to-purple-900/30 border border-gray-300/50 dark:border-gray-600/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 hover:scale-105 transform disabled:hover:scale-100 shadow-sm hover:shadow-md"
              >
                Sonraki
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Profile Tree Modal */}
      {profileTreeModal.isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div
              className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-75"
              onClick={closeProfileTreeModal}
            ></div>

            {/* Modal panel */}
            <div className="inline-block align-bottom glass-strong rounded-3xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full animate-scale-in">
              <div className="glass-strong px-6 py-4 border-b border-gray-200/50 dark:border-gray-700/50 bg-gradient-to-r from-indigo-50/50 via-purple-50/50 to-pink-50/50 dark:from-indigo-900/20 dark:via-purple-900/20 dark:to-pink-900/20">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-slate-800 dark:text-white">
                    Profile Tree
                  </h3>
                  <button
                    onClick={closeProfileTreeModal}
                    className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 transition-colors"
                  >
                    {renderIcon(FiX, 'w-6 h-6')}
                  </button>
                </div>
                <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                  Player ID: {profileTreeModal.playerId}
                </p>
              </div>

              <div className="glass px-6 py-4 max-h-[600px] overflow-y-auto">
                {profileTreeModal.loading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Test Data Section */}
                    <div>
                      <h4 className="text-md font-semibold text-indigo-600 dark:text-indigo-400 mb-4 pb-2 border-b border-indigo-200 dark:border-indigo-700">
                        Test Verileri
                      </h4>
                      {profileTreeModal.testData && Object.keys(profileTreeModal.testData).length > 0 ? (
                        <div className="space-y-3">
                          {Object.entries(profileTreeModal.testData).map(([key, value]) => {
                            // Handle different value types
                            let displayValue: string;
                            if (value === null || value === undefined) {
                              displayValue = '-';
                            } else if (Array.isArray(value)) {
                              displayValue = value.length > 0 ? value.join(', ') : '-';
                            } else if (typeof value === 'object') {
                              displayValue = JSON.stringify(value);
                            } else {
                              displayValue = String(value);
                            }

                            return (
                              <div
                                key={key}
                                className="flex items-center justify-between p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border border-indigo-100 dark:border-indigo-800"
                              >
                                <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{key}</span>
                                <span className="text-sm font-semibold text-indigo-600 dark:text-indigo-400">{displayValue}</span>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-slate-500 dark:text-slate-400 text-sm">
                          Test verisi bulunamadı
                        </div>
                      )}
                    </div>

                    {/* Profile Data Section */}
                    <div>
                      <h4 className="text-md font-semibold text-purple-600 dark:text-purple-400 mb-4 pb-2 border-b border-purple-200 dark:border-purple-700">
                        Profile Verileri
                      </h4>
                      {profileTreeModal.profileData && Object.keys(profileTreeModal.profileData).length > 0 ? (
                        <div className="space-y-3">
                          {Object.entries(profileTreeModal.profileData).map(([key, value]) => {
                            // Handle different value types
                            let displayValue: string;
                            if (value === null || value === undefined) {
                              displayValue = '-';
                            } else if (Array.isArray(value)) {
                              displayValue = value.length > 0 ? value.join(', ') : '-';
                            } else if (typeof value === 'object') {
                              displayValue = JSON.stringify(value);
                            } else {
                              displayValue = String(value);
                            }

                            return (
                              <div
                                key={key}
                                className="flex items-center justify-between p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-100 dark:border-purple-800"
                              >
                                <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{key}</span>
                                <span className="text-sm font-semibold text-purple-600 dark:text-purple-400">{displayValue}</span>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-slate-500 dark:text-slate-400 text-sm">
                          Profile verisi bulunamadı
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="glass-strong px-6 py-3 border-t border-gray-200/50 dark:border-gray-600/50 bg-gradient-to-r from-gray-50/50 to-white/50 dark:from-gray-800/50 dark:to-gray-700/50">
                <button
                  onClick={closeProfileTreeModal}
                  className="w-full px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 dark:from-indigo-500 dark:via-purple-500 dark:to-pink-500 rounded-xl hover:from-indigo-700 hover:via-purple-700 hover:to-pink-700 dark:hover:from-indigo-600 dark:hover:via-purple-600 dark:hover:to-pink-600 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 transform"
                >
                  Kapat
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Counter Modal - Portal to body for fullscreen */}
      {showCounter && createPortal(
        <div className="fixed inset-0 z-[99999] w-full h-full bg-gray-900 dark:bg-gray-950 flex items-center justify-center overflow-hidden" style={{ top: 0, left: 0, right: 0, bottom: 0 }}>
          <button
            onClick={() => setShowCounter(false)}
            className="absolute top-6 right-6 p-3 text-white dark:text-gray-200 hover:bg-white/10 dark:hover:bg-gray-800 rounded-lg transition-colors z-10"
            aria-label="Kapat"
          >
            {renderIcon(FiX, "w-6 h-6")}
          </button>
          
          <div className="text-center w-full h-full flex flex-col items-center justify-center px-4">
            <div className="mb-8">
              <p className="text-gray-300 dark:text-gray-400 text-lg mb-2">Toplam Kullanıcı Sayısı</p>
              <div 
                key={totalUsers}
                className="font-bold text-white dark:text-gray-100 mb-4"
                style={{
                  fontSize: 'clamp(6rem, 20vw, 15rem)',
                  lineHeight: '1',
                  animation: previousCounterValue < counterValue ? 'counterBounce 0.6s ease-out' : 'none',
                }}
              >
                {totalUsers.toLocaleString('tr-TR')}
              </div>
            </div>
            
            <div className="mt-12">
              <p className="text-gray-400 dark:text-gray-500 text-sm">
                Her 5 saniyede bir güncelleniyor
              </p>
            </div>
          </div>
          
          <style>{`
            @keyframes counterBounce {
              0% {
                transform: scale(1);
              }
              50% {
                transform: scale(1.2);
                color: #10b981;
              }
              100% {
                transform: scale(1);
              }
            }
          `}</style>
        </div>,
        document.body
      )}
    </div>
  );
};

export default Users;

